/**
 * Analytics event tracking for EAS forms
 *
 * Analytics is optional - if @gartner-digital-markets/analytics is not installed,
 * events are pushed directly to dataLayer (GTM).
 */

import type { AnalyticsFormName, FormCloseDetail } from '../core';
import { sendEvent } from './send-event';

const BASE_EVENT_PARAMS = {
  name: 'email_capture',
  form_type: 'email-form',
} as const;

interface TrackingParams {
  form_id: string;
  form_name?: AnalyticsFormName;
}

const buildBaseEventParams = (params: TrackingParams) => ({
  ...BASE_EVENT_PARAMS,
  form_name: params.form_name ?? 'inline',
});

/**
 * Track when form fails to load correctly
 */
export const trackFormFailedToDisplay = (params: TrackingParams) => {
  sendEvent({
    ...buildBaseEventParams(params),
    form_step_name: 'form-try-to-display',
    form_step_details: '',
    form_step_number: 1,
    ...params,
  });
};

/**
 * Track when form is revealed (user scrolled down to the form)
 */
export const trackFormOpen = (params: TrackingParams) => {
  sendEvent({
    ...buildBaseEventParams(params),
    form_step_name: 'form-open',
    form_step_details: 'revealed',
    form_step_number: 1,
    ...params,
  });
};

/**
 * Track when form is displayed with email prefilled
 */
export const trackFormOpenEmailPrefilled = (params: TrackingParams) => {
  sendEvent({
    ...buildBaseEventParams(params),
    form_step_name: 'form-open',
    form_step_details: 'email-prefilled',
    form_step_number: 1,
    ...params,
  });
};

/**
 * Track when user mouses over the form
 */
export const trackFormMouseover = (params: TrackingParams) => {
  sendEvent({
    ...buildBaseEventParams(params),
    form_step_name: 'form-mouseover',
    form_step_details: '',
    form_step_number: 1,
    ...params,
  });
};

/**
 * Track when user clicks on the email box
 */
export const trackFormStartedEmailBoxClick = (params: TrackingParams) => {
  sendEvent({
    ...buildBaseEventParams(params),
    form_step_name: 'form-started',
    form_step_details: 'email-box-click',
    form_step_number: 1,
    ...params,
  });
};

/**
 * Track when user starts entering email
 */
export const trackFormStartedEmailBoxType = (params: TrackingParams) => {
  sendEvent({
    ...buildBaseEventParams(params),
    form_step_name: 'form-started',
    form_step_details: 'email-box-type',
    form_step_number: 1,
    ...params,
  });
};

/**
 * Track when submit button is not working (validation error)
 */
export const trackFormFailedSubmit = (params: TrackingParams) => {
  sendEvent({
    ...buildBaseEventParams(params),
    form_step_name: 'form-failed-submit',
    form_step_details: 'email-input',
    form_step_number: 1,
    ...params,
  });
};

/**
 * Track when user submits the email (Step 1)
 */
export const trackFormSubmitted = (
  params: TrackingParams & { cta_text?: string }
) => {
  sendEvent({
    ...buildBaseEventParams(params),
    form_step_name: 'form-submitted',
    form_step_details: '',
    form_step_number: 1,
    ...params,
  });
};

/**
 * Track when micro-data question is displayed (Step 2)
 */
export const trackMicroDataQuestionDisplayed = (params: TrackingParams) => {
  sendEvent({
    ...buildBaseEventParams(params),
    form_step_name: 'form-micro-data-question',
    form_step_details: 'micro-data-question',
    form_step_number: 2,
    ...params,
  });
};

/**
 * Track when micro-data question is answered/submitted (Step 2)
 */
export const trackMicroDataQuestionSubmitted = (params: TrackingParams) => {
  sendEvent({
    ...buildBaseEventParams(params),
    form_step_name: 'form-micro-data-question-submitted',
    form_step_details: 'micro-data-question',
    form_step_number: 2,
    cta_text: 'Submit',
    ...params,
  });
};

/**
 * Track when micro-data question submission fails (Step 2)
 */
export const trackMicroDataQuestionFailedSubmit = (params: TrackingParams) => {
  sendEvent({
    ...buildBaseEventParams(params),
    form_step_name: 'form-micro-data-question-failed-submit',
    form_step_details: 'micro-data-question',
    form_step_number: 2,
    ...params,
  });
};

/**
 * Track when user leaves the page
 */
export const trackFormClosed = (
  params: TrackingParams & {
    form_step_number: 1 | 2;
    form_step_details?: FormCloseDetail;
  }
) => {
  sendEvent({
    ...buildBaseEventParams(params),
    form_step_name: 'form-closed',
    form_step_details: params.form_step_details ?? 'close-page',
    ...params,
  });
};
